import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import { codeAppliedState, planState, subscriptionState, themeState } from "@/lib/store";
import PaymentStripeForm from "@/components/payment-stripe-form/PaymentStripeForm";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { makePayment } from "@/controllers/subscription";
import { useMedia } from "react-use";
import { Box, Button, Skeleton, Typography } from "@mui/material";

interface PaymentModalStripeProps {
    payment: {};
    setStep: Dispatch<SetStateAction<number>>;
    disabled: boolean;
    setDisabled: Dispatch<SetStateAction<boolean>>;
    firstNameInvalid: boolean;
    setFirstNameInvalid: Dispatch<SetStateAction<boolean>>;
    lastNameInvalid: boolean;
    setLastNameInvalid: Dispatch<SetStateAction<boolean>>;
}

const PaymentStripe: FC<PaymentModalStripeProps> = ({ payment, setStep, disabled, setDisabled, firstNameInvalid, setFirstNameInvalid, lastNameInvalid, setLastNameInvalid }) => {
    const theme = useRecoilValue(themeState);
    const [clientSecret, setClientSecret] = useState<string>();
    const [loading, setLoading] = useState(true);
    const plan = useRecoilValue(planState);
    const subscription = useRecoilValue(subscriptionState);
    const codeApplied = useRecoilValue(codeAppliedState);

    const isMobile = useMedia("(max-width: 768px)");

    const stripe = useMemo(() => loadStripe(subscription.payment_data.stripe_public_key), [subscription]);

    useEffect(() => {
        if (plan?.plan_id != null) {
            setLoading(true);
            makePayment({ priceId: plan?.plan_id, discount_code: codeApplied })
                .then((res) => setClientSecret(res.clientSecret))
                .catch((err) => console.error("Error on Payment Stripe:", err))
                .finally(() => setLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plan?.plan_id, codeApplied]);

    if (loading)
        return (
            <Box sx={{ p: 1 }}>
                <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: "100%" }}>
                        <Skeleton width={45} height={30} />
                        <Skeleton width={isMobile ? "100%" : 200} height={60} />
                    </Box>
                    <Box sx={{ width: "100%" }}>
                        <Skeleton width={45} height={30} />
                        <Skeleton width={isMobile ? "100%" : 200} height={60} />
                    </Box>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                    <Skeleton width={80} height={90} />
                    <Skeleton width={80} height={90} />
                    <Skeleton width={80} height={90} />
                    <Skeleton width={80} height={90} />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: "100%" }}>
                        <Skeleton width={45} height={30} />
                        <Skeleton width={"100%"} height={60} />
                    </Box>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 1 }}>
                    <Box>
                        <Skeleton width={45} height={30} />
                        <Skeleton width={200} height={60} />
                    </Box>
                    <Box>
                        <Skeleton width={45} height={30} />
                        <Skeleton width={200} height={60} />
                    </Box>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 1 }}>
                    <Skeleton width={"100%"} height={60} />
                </Box>
            </Box>
        );
    else if (!clientSecret)
        return (
            <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography color="error" gutterBottom>
                    Unable to initialize payment. Please try again.
                </Typography>
                <Button
                    variant="outlined"
                    onClick={() => {
                        setLoading(true);
                        makePayment({ priceId: plan?.plan_id, discount_code: codeApplied })
                            .then((res) => setClientSecret(res.clientSecret))
                            .catch((err) => console.error("Error on Payment Stripe:", err))
                            .finally(() => setLoading(false));
                    }}
                >
                    Retry
                </Button>
            </Box>
        );
    else
        return (
            <Elements
                key={clientSecret}
                stripe={stripe}
                options={{
                    clientSecret,
                    appearance: { theme: theme === "dark" ? "night" : "stripe" },
                    loader: "auto",
                }}
            >
                <PaymentStripeForm
                    payment={payment}
                    setStep={setStep}
                    disabled={disabled}
                    setDisabled={setDisabled}
                    firstNameInvalid={firstNameInvalid}
                    setFirstNameInvalid={setFirstNameInvalid}
                    lastNameInvalid={lastNameInvalid}
                    setLastNameInvalid={setLastNameInvalid}
                    plan={plan}
                />
            </Elements>
        );
};

export default PaymentStripe;
